export enum ConversationOutcome {
	SUCCESS = 'success',
	PARTIAL = 'partial',
	VOICEMAIL_LEFT = 'voicemail_left',
	MACHINE_ANSWERED = 'machine_answered',
	NO_ANSWER = 'no_answer',
	FAILED = 'failed',
	CALLBACK_SCHEDULED = 'callback_scheduled',
	NOT_INTERESTED = 'not_interested',
	WRONG_NUMBER = 'wrong_number',
	BUSY = 'busy',
}

export type ConversationOutcomeKey = keyof typeof ConversationOutcome;
export type ConversationOutcomeValue =
	(typeof ConversationOutcome)[ConversationOutcomeKey];

/**
 * Human-readable labels for conversation outcomes
 */
export const ConversationOutcomeLabels: Record<string, string> = {
	[ConversationOutcome.SUCCESS]: 'Success',
	[ConversationOutcome.PARTIAL]: 'Partial',
	[ConversationOutcome.VOICEMAIL_LEFT]: 'Voicemail Left',
	[ConversationOutcome.MACHINE_ANSWERED]: 'Machine Answered',
	[ConversationOutcome.NO_ANSWER]: 'No Answer',
	[ConversationOutcome.FAILED]: 'Failed',
	[ConversationOutcome.CALLBACK_SCHEDULED]: 'Callback Scheduled',
	[ConversationOutcome.NOT_INTERESTED]: 'Not Interested',
	[ConversationOutcome.WRONG_NUMBER]: 'Wrong Number',
	[ConversationOutcome.BUSY]: 'Busy',
};

/**
 * Get human-readable outcome label from outcome code
 */
export function getConversationOutcomeLabel(
	outcome: string | null | undefined,
): string {
	if (outcome == null) {
		return '-';
	}

	return ConversationOutcomeLabels[outcome] ?? outcome;
}

/**
 * Get outcome color based on outcome code
 */
export function getConversationOutcomeColor(
	outcome: string | null | undefined,
): string {
	if (outcome == null) {
		return 'default';
	}

	switch (outcome) {
		case ConversationOutcome.SUCCESS:
			return 'success';
		case ConversationOutcome.PARTIAL:
			return 'info';
		case ConversationOutcome.VOICEMAIL_LEFT:
			return 'warning';
		case ConversationOutcome.MACHINE_ANSWERED:
			return 'warning';
		case ConversationOutcome.NO_ANSWER:
			return 'secondary';
		case ConversationOutcome.FAILED:
			return 'danger';
		case ConversationOutcome.CALLBACK_SCHEDULED:
			return 'info';
		case ConversationOutcome.NOT_INTERESTED:
			return 'danger';
		case ConversationOutcome.WRONG_NUMBER:
			return 'danger';
		case ConversationOutcome.BUSY:
			return 'warning';
		default:
			return 'default';
	}
}

/**
 * All outcome values as an array for form dropdowns
 */
export const ConversationOutcomeOptions: Array<{
	value: string;
	label: string;
}> = Object.values(ConversationOutcome).map((value) => ({
	value,
	label: ConversationOutcomeLabels[value] ?? value,
}));
